import {
  getCompanyUploaderReport,
  getKycConsiderResult,
  getKycDocumentStatus,
  KYC_TOOLTIP_PRIORITY,
} from "./common";
import { getKycScoreState } from "./profileCompletionScore";

// Company login tenants (KYB) only. Reasons shown next to the KYB and
// authorised signatory statuses. The person reason follows the same checks,
// in the same order, as the tenant KYC reason in TenantHeaderCard; the tenant
// flow does not use this file.

const getPersonName = (person) =>
  person?.kyc_data?.full_name ||
  [person?.kyc_data?.first_name, person?.kyc_data?.last_name]
    .filter(Boolean)
    .join(" ") ||
  person?.name ||
  "-";

const getPersonReason = (person, _t, llShowOnly) => {
  const kycChecks = person?.kyc_checks;
  if (
    getKycDocumentStatus({ onfido_user_kyc: person }) === "review" &&
    person?.kyc_data?.document_type === "unidentified"
  ) {
    return llShowOnly
      ? _t("onfidoUnidentifiedDocumentReviewLandlordMessage")
      : _t("onfidoUnidentifiedDocumentReviewTenantMessage");
  }
  if (!kycChecks) return _t("onfidoFallbackMessage");
  if (
    getKycConsiderResult(
      kycChecks,
      "image_integrity.supported_document.properties.issuing_country_subject_to_sanctions"
    )
  ) {
    return _t("onfidoImageIntegritySanctionsMessage");
  }
  for (const { path, message, explanation } of KYC_TOOLTIP_PRIORITY) {
    if (getKycConsiderResult(kycChecks, path)) {
      return path.includes("image_quality")
        ? `${_t(message)}: ${_t(explanation)}`
        : _t(message);
    }
  }
  // Review or declined with clear document checks (for example AML).
  return _t("onfidoFallbackMessage");
};

// One "Name: reason" line per signatory in review or declined.
export const getKybPeopleReasons = (user, _t, llShowOnly) =>
  (user?.onfido_user_kyc?.kyb_people || [])
    .filter((person) =>
      ["review", "declined"].includes(
        getKycScoreState({ onfido_user_kyc: person })
      )
    )
    .map(
      (person) =>
        `${getPersonName(person)}: ${getPersonReason(person, _t, llShowOnly)}`
    );

// KYB status reasons: the signatories' reasons, or a company message when
// the review / decline is not caused by a signatory.
export const getKybReasons = (user, _t, llShowOnly) => {
  const state = getKycScoreState(user);
  if (!["review", "declined"].includes(state)) return [];
  const peopleReasons = getKybPeopleReasons(user, _t, llShowOnly);
  if (peopleReasons.length) return peopleReasons;
  return [
    state === "declined"
      ? _t(
          "kybCompanyDeclinedReason",
          "Company KYB status tooltip: reason when the company check is declined and no signatory caused it"
        )
      : _t(
          "kybCompanyReviewReason",
          "Company KYB status tooltip: reason when the company check is in review and no signatory caused it"
        ),
  ];
};

// Authorised signatories card: ticks come from the company's own Skor Lite
// profile (legal_entity marked as the uploader), report.authorizedSignatories
// and report.companyOwners. The DNI / NIE gets a tick when the ID number is
// found there; the name gets a tick when the name matches, ignoring case,
// accents, punctuation, joining words and word order, with at most one word
// extra or missing.
const NAME_PARTICLES = new Set([
  "de",
  "del",
  "la",
  "las",
  "los",
  "y",
  "i",
  "e",
]);

const nameWords = (name) =>
  String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .split(" ")
    .filter((word) => word && !NAME_PARTICLES.has(word));

const normaliseId = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

const namesMatch = (a, b) => {
  const wordsA = new Set(nameWords(a));
  const wordsB = new Set(nameWords(b));
  if (!wordsA.size || !wordsB.size) return false;
  const shared = [...wordsA].filter((word) => wordsB.has(word)).length;
  const longest = Math.max(wordsA.size, wordsB.size);
  if (longest === 1) return shared === 1;
  return shared >= 2 && shared >= longest - 1;
};

export const getCompanySignatoryRecords = (user) => {
  const ownProfile = getCompanyUploaderReport(
    user?.tenant_extra_field_id?.forensic_analysis?.profileReports ?? []
  );
  const report = ownProfile?.report;
  // Skor Lite sends { signatories: [...] } / { owners: [...] } (with the
  // documents they were found in); a plain list is accepted too.
  const listOf = (value, key) =>
    Array.isArray(value)
      ? value
      : Array.isArray(value?.[key])
        ? value[key]
        : [];
  return [
    ...listOf(report?.authorizedSignatories, "signatories"),
    ...listOf(report?.companyOwners, "owners"),
  ].filter((record) => record && typeof record === "object");
};

// What matched, so each field gets its own tick: { id, name }.
// names: every name known for the person; idNumber: their DNI / NIE /
// passport number if known.
export const getSignatoryMatch = (records, { names = [], idNumber }) => {
  if (!records?.length) return { id: false, name: false };
  const id = normaliseId(idNumber);
  return {
    id: !!id && records.some((record) => normaliseId(record?.id_number) === id),
    name: names.some(
      (name) => name && records.some((record) => namesMatch(name, record?.name))
    ),
  };
};
